import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export const api = axios.create({
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    withXSRFToken: true,
});

interface ConfigSource {
    (): {
        URL?: string;
        proxyURL?: string;
        onAuthError: () => void;
        setApiVersion: (version: string) => void;
    };
}

const intercept = (
    instance: AxiosInstance,
    urlKey: string,
    configSource: ConfigSource,
) => {
    instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        const { ...conf } = configSource();

        if (conf[urlKey]) {
            config.baseURL = conf[urlKey];
        }

        return config as InternalAxiosRequestConfig;
    });

    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        (error) => {
            const { onAuthError } = configSource();

            if (!error.response || !error.response.status) {
                return Promise.reject(error);
            }

            if (error.response.status === 401) {
                onAuthError();
                return Promise.reject(error);
            }

            return Promise.reject(error);
        },
    );
};

export const start = (configSource: ConfigSource) => {
    intercept(api, 'URL', configSource);

    // retry with new csrf token if csrf token rejected
    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            return Promise.reject(error);
        },
    );

    api.interceptors.response.use((response) => {
        const version = response.headers['x-api-version'] || null;
        if (!version) {
            return response;
        }

        const { setApiVersion } = configSource();
        setApiVersion(version);
        return response;
    });
};

type CSRFTokenResponse = {
    token: string;
};

export const refreshCSRFToken = async () => {
    await api.get<CSRFTokenResponse>('/auth/csrf-token');
};
